import { type ChangeEvent, useEffect, useRef, useState } from "react";
import { t } from "ttag";

import Button from "metabase/common/components/Button";
import { Sortable } from "metabase/common/components/Sortable";
import type { TabButtonMenuItem } from "metabase/common/components/TabButton";
import { TabButton } from "metabase/common/components/TabButton";
import { TabRow } from "metabase/common/components/TabRow";
import { Box, Flex, Menu } from "metabase/ui";

import { MAX_TABS } from "../constants";
import { useWorkbenchActions } from "../context";
import type { AdhocTabId, PersistedAdhocTab } from "../types";

import S from "./WorkbenchTabs.module.css";

export type WorkbenchTabsProps = {
  tabs: PersistedAdhocTab[];
  activeTabId: AdhocTabId | null;
};

/** The top-level query tab strip: rename, reorder, duplicate, close, add. */
export function WorkbenchTabs({ tabs, activeTabId }: WorkbenchTabsProps) {
  const actions = useWorkbenchActions();

  return (
    <Flex align="start" gap="sm" w="100%" className={S.tabBar}>
      <TabRow<AdhocTabId | null>
        value={activeTabId}
        onChange={(value) => value != null && actions.selectTab(value)}
        itemIds={tabs.map((tab) => tab.id)}
        handleDragEnd={(activeId, overId) =>
          actions.moveTab(String(activeId), String(overId))
        }
      >
        {tabs.map((tab) => (
          <WorkbenchTab key={tab.id} tab={tab} canClose={tabs.length > 1} />
        ))}
        <Button
          icon="add"
          iconSize={12}
          onClick={() => actions.addTab()}
          disabled={tabs.length >= MAX_TABS}
          aria-label={t`New query tab`}
          className={S.addTabButton}
        />
      </TabRow>
    </Flex>
  );
}

type WorkbenchTabProps = {
  tab: PersistedAdhocTab;
  canClose: boolean;
};

type TabAction = {
  key: string;
  label: string;
  perform: () => void;
};

/**
 * One query tab.
 *
 * Uses the *controlled* variant of the shared `TabButton` — rather than
 * `TabButton.Renameable`, which owns its renaming state privately — so that
 * renaming can also be started from the right-click menu.
 */
function WorkbenchTab({ tab, canClose }: WorkbenchTabProps) {
  const actions = useWorkbenchActions();

  const [isRenaming, setIsRenaming] = useState(false);
  const [draftName, setDraftName] = useState(tab.name);
  const [contextMenuPosition, setContextMenuPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setDraftName(tab.name);
  }, [tab.name]);

  useEffect(() => {
    if (isRenaming) {
      inputRef.current?.focus();
    }
  }, [isRenaming]);

  const finishRenaming = () => {
    const trimmed = draftName.trim();
    if (trimmed === "") {
      setDraftName(tab.name);
    } else if (trimmed !== tab.name) {
      actions.renameTab(tab.id, trimmed);
    }
    setIsRenaming(false);
  };

  const tabActions: TabAction[] = [
    { key: "rename", label: t`Rename`, perform: () => setIsRenaming(true) },
    {
      key: "duplicate",
      label: t`Duplicate`,
      perform: () => actions.duplicateTab(tab.id),
    },
  ];
  if (canClose) {
    tabActions.push(
      {
        key: "close",
        label: t`Close`,
        perform: () => actions.closeTab(tab.id),
      },
      {
        key: "close-others",
        label: t`Close other tabs`,
        perform: () => actions.closeOtherTabs(tab.id),
      },
    );
  }

  // The same actions are reachable from the chevron dropdown that the shared
  // TabButton renders for us.
  const menuItems: TabButtonMenuItem[] = tabActions.map((action) => ({
    label: action.label,
    action: () => action.perform(),
  }));

  return (
    <Sortable id={tab.id} disabled={isRenaming}>
      <Box
        onContextMenu={(event) => {
          if (isRenaming) {
            // Leave the browser's own menu to the rename input.
            return;
          }
          event.preventDefault();
          setContextMenuPosition({ x: event.clientX, y: event.clientY });
        }}
      >
        <TabButton
          ref={inputRef}
          value={tab.id}
          label={draftName}
          isRenaming={isRenaming}
          menuItems={menuItems}
          onRename={(event: ChangeEvent<HTMLInputElement>) =>
            setDraftName(event.target.value)
          }
          onFinishRenaming={finishRenaming}
          onInputDoubleClick={() => setIsRenaming(true)}
        />
      </Box>
      {/*
       * The menu is anchored to a zero-sized element placed at the pointer
       * rather than to the tab itself, so it opens where the user clicked and
       * a plain left-click on the tab never triggers it.
       */}
      <Menu
        opened={contextMenuPosition != null}
        onChange={(opened) => !opened && setContextMenuPosition(null)}
        position="bottom-start"
        shadow="md"
        trapFocus={false}
      >
        <Menu.Target>
          <Box
            pos="fixed"
            w={1}
            h={1}
            left={contextMenuPosition?.x ?? 0}
            top={contextMenuPosition?.y ?? 0}
            style={{ pointerEvents: "none" }}
            aria-hidden
          />
        </Menu.Target>
        <Menu.Dropdown data-testid="adhoc-tab-context-menu">
          {tabActions.map((action) => (
            <Menu.Item
              key={action.key}
              onClick={() => {
                setContextMenuPosition(null);
                action.perform();
              }}
            >
              {action.label}
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
    </Sortable>
  );
}
